import { MutableRefObject, useCallback, useMemo, useRef } from "react";
import { OverviewType, useAppDispatch } from "../../app";
import { buildFilterUpdateCalls } from "../utils/columnItemMetaDataHelpers";
import { updateFilterArray } from "../../common/dataRefining/store/dataRefiningSlice";
import { ColumnItem } from "../interfaces/overview";
import { GetRef, Select } from "antd";
import useFocus from "./useFocus";

type SelectRef = GetRef<typeof Select>;

const useApiFilterableItem = (
  columnItem: ColumnItem,
  overviewType: OverviewType,
  isOpen: boolean,
  isBlankEntriesChecked: boolean,
  blankValue: string,
  values: Array<string> | undefined,
  searchValue: string | undefined,
  handleSearch: (searchValue: string, values?: string[] | undefined) => void,
  mode?: "multiple",
) => {
  const dispatch = useAppDispatch();

  const ref: MutableRefObject<null | SelectRef> = useRef(null);

  const { open, setOpen } = useFocus(ref, isOpen);

  const selectValue = useMemo(() => {
    if (isBlankEntriesChecked) {
      return values?.filter((value) => value !== blankValue);
    }

    return values;
  }, [values, isBlankEntriesChecked, blankValue]);

  const handleChange = useCallback(
    (value: string | Array<string>) => {
      let updatedValues: Array<string> | string = value;
      if (isBlankEntriesChecked) {
        updatedValues = Array.isArray(value)
          ? [...value, blankValue]
          : [value, blankValue];
      }

      const updateCalls = buildFilterUpdateCalls(
        updatedValues,
        columnItem.metadata,
        overviewType,
      );

      updateCalls.forEach((uc) => {
        dispatch(updateFilterArray(uc));
      });

      // In multiple mode keep the current search results so the user can keep
      // picking matches (e.g. all "NAU" certificates) without re-typing. For
      // single select we reset the search after a pick as before.
      if (searchValue && mode !== "multiple") {
        handleSearch("");
      }
    },
    [
      dispatch,
      columnItem.metadata,
      overviewType,
      isBlankEntriesChecked,
      blankValue,
      handleSearch,
      searchValue,
      mode,
    ],
  );

  return {
    ref,
    selectValue,
    handleChange,
    open,
    setOpen,
  };
};

export default useApiFilterableItem;
